/**
 * Helpers for comparing versions of a component's ingredient list
 * (a meal's meal_food rows vs the component's defaults, or vs another
 * component). Comparisons are done per 100g, matching MealAudit.
 */

export function normaliseTo100g(ingredients) {
  const sum = ingredients.reduce((sum, i) => i.amount + sum, 0);
  if (sum === 0) {
    return ingredients.map(i => ({...i, amount: '0.00'}));
  }
  const ratio = 100 / sum;
  return ingredients.map(i => ({...i, amount: (i.amount * ratio).toFixed(2)}));
}

// per-100g tolerance so float noise doesn't read as a recipe change
const AMOUNT_TOLERANCE = 0.5;

function amountsDiffer(a, b) {
  return Math.abs(Number(a) - Number(b)) > AMOUNT_TOLERANCE;
}

/**
 * Annotates `ingredients` against `reference` (both per-100g lists):
 * items are marked changed when new or with a different amount, and the
 * reference items missing from `ingredients` are returned as removed.
 */
export function compareIngredients(ingredients, reference) {
  const items = ingredients.map(ingredient => {
    const match = reference.find(r => r.name === ingredient.name);
    const changed = !match || amountsDiffer(match.amount, ingredient.amount);
    return {...ingredient, changed};
  });
  const names = new Set(ingredients.map(i => i.name));
  const removed = reference.filter(r => !names.has(r.name));
  return {items, removed};
}

/** true when the per-100g lists are the same recipe */
export function ingredientsMatch(a, b) {
  const {items, removed} = compareIngredients(a, b);
  return removed.length === 0 && items.every(i => !i.changed);
}

/**
 * Cost of an ingredient list in dollars. `price` on an ingredient is
 * food_name.price, cents per kg. Ingredients without a price are costed at
 * zero and returned in `unpriced` so callers can warn about them.
 */
export function ingredientCost(ingredients) {
  const priced = ingredients.filter(i => i.price);
  const unpriced = ingredients.filter(i => !i.price);
  const cost = priced.reduce((sum, i) => sum + (Number(i.amount) / 1000) * (i.price / 100), 0);
  return {cost, hasAnyPrice: priced.length > 0, unpriced};
}

/** dollars with 2 decimals; toFixed alone truncates halves (0.475 -> "0.47") */
export function formatDollars(value) {
  return (Math.round(value * 100) / 100).toFixed(2);
}
